// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The cabinet, procedurally, at true scale (M3-03).
//
// Built from the definition's own `dimensions.cabinet` block rather than from numbers typed
// here, because the cabinet is what gives the player their sense of scale: get it wrong and a
// 27 mm ball looks like a marble on a dining table, and every camera preset is wrong with it.
//
// **The silhouette is the thing.** Seen from the side a Williams-era cabinet is not a box — it
// is a quadrilateral with a level bottom, a vertical front, a taller vertical back, and a top
// edge that slopes up from front to back at the playfield's own angle. That sloping top edge is
// the single line that makes a rendering read as *a pinball machine* rather than as a table
// with a picture on it, so the side panels are extruded shapes and not boxes.
//
// World frame here, not playfield-local: +X right, +Y up, +Z toward the player, origin on the
// floor at the centre of the cabinet's footprint. The playfield's own frame (PH-001) is a child
// transform, applied in `MachineScene`.

import {
  BoxGeometry,
  BufferGeometry,
  CatmullRomCurve3,
  CylinderGeometry,
  ExtrudeGeometry,
  Group,
  Mesh,
  MeshStandardMaterial,
  Shape,
  SphereGeometry,
  TubeGeometry,
  Vector3,
  type Material,
} from 'three'
import {assembleCabinetPlunger} from './componentPlungerFactory'
import {assembleCabinetControls} from './cabinetControlAssembly'
import { authoredCabinetFor, addAuthoredCabinet } from './authoredCabinets'
import { authoredGlazingFor, addAuthoredGlazing } from './authoredGlazing'
import { authoredBackbox } from './authoredBackbox'
import { addFloorContact } from './floorContact'
import { addCabinetLegs, cabinetLegsReady } from './cabinetLegs'
import {cabinetTrimFor,addCabinetTrim} from './cabinetTrim'
import reflectionFinish from '../../SourceArt/PierContact/reflection-finish.json'
import { MeshPhysicalMaterial } from 'three'
import { FINISHES, PartPalette } from './partsKit'
import type { MachineDefinition } from '@core/machine/types'
import { BALL_RADIUS } from '@core/sim/constants'
import {
  BUTTON_RADIUS,
  BUTTON_SET_BACK,
  FLIPPER_CAP_RADIUS,
  GAUGE_OFF,
  PLUNGER_SPRING_WIRE,
  PLUNGER_TIP_RADIUS,
  PULL_GAUGE_DEPTH,
  PULL_GAUGE_HEIGHT,
  PULL_GAUGE_WIDTH,
  cabinetControlsFor,
  plungerSpringOf,
  plungerSpringPath,
  pullGaugeOf,
  type PressableId,
} from '../xr/cabinetControls'
import { LAMP_GLOW_OFF, LAMP_OFF, LAMP_OVER, START_GLOW_REST } from './controlPicture'

export interface CabinetDimensions {
  readonly playfieldRecess?: number
  readonly glassHeight?: number
  readonly playfieldWidth?: number
  /** Front-to-back, metres. A standard body is 1.295. */
  readonly length: number
  /** Side to side, metres. 0.724 across the outside of the side panels. */
  readonly width: number
  /** Floor to the top of the backbox. */
  readonly height: number
  /** Floor to the top of the lockdown bar — the height a player's hands rest at. */
  readonly lockdownBarHeight: number
  /** Playfield incline, radians. The top edge of each side panel follows it. */
  readonly incline: number
  /** Playfield length, so the panels are cut to contain it rather than to a guessed number. */
  readonly playfieldLength: number
  /**
   * Where the shooter lane is across the machine, metres from the centre line.
   *
   * **Read from the machine's own plunger device rather than guessed from the cabinet.** The rod
   * a player pulls has to be in line with the lane it launches into, and NEON NOVA's shooter sits
   * at x = 0.225 while a cabinet-relative guess of `width / 2 − 0.075` puts it at 0.287 — 62 mm
   * out, which from a headset reads as a plunger that does not line up with the machine's own,
   * because it does not. Optional so a definition that states no plunger still gets a rod: the
   * cabinet has a hole in the front either way, and a hole with nothing in it looks broken.
   */
  readonly shooterX?: number
  /**
   * The height of the ball resting in the shooter lane, metres from the floor — where the rod
   * has to be to push it (14 Sep 2026). Derived from the playfield exactly as `buildCabinet`
   * places it (`playfieldCentreY`), plus the lane's own distance up the incline and a ball's
   * radius: 0.914 on NEON NOVA, where `lockdownBarHeight − 0.045` had put the rod at 0.869.
   * Optional for the same reason as `shooterX`.
   */
  readonly shooterY?: number
  /**
   * The ball's resting centre in the shooter lane, metres from the cabinet's centre along z
   * (+z toward the player): the lane's own y down the incline. With `shooterY`, the point the
   * rod is aimed through.
   */
  readonly shooterZ?: number
}

export interface CabinetBuild {
  readonly group: Group
  /** Where to put the playfield's centre, in world space, and how far to tilt it. */
  readonly playfieldCentre: { readonly y: number; readonly z: number }
  readonly geometries: readonly BufferGeometry[]
  readonly materials: readonly Material[]
  /**
   * The body's own paint: what the room's light tints (M9-51, 06 §3).
   *
   * **A named subset rather than `materials`**, and the exclusions are the point. The backglass is
   * *emissive* — it is a lit panel, and a room tint on a light source says the room is colouring
   * something that is emitting rather than reflecting. The parts palette is the playfield's, under
   * glass, where a room's ambient does not reach. What is left is the surfaces a player would
   * actually see the room's colour on: the body, its two flanks, the trim and the dark furniture.
   */
  readonly tintable: readonly MeshStandardMaterial[]
  /**
   * The lit glass at the top, if this machine has art for one.
   *
   * Absent rather than empty when the machine states no backglass art, because a backbox with
   * no picture on it has no surface to fit a display to either — and a display placed against
   * the *backbox's* dimensions instead would be 50 mm wider than the aperture it is supposed
   * to sit inside, which reads as a display that does not fit its own machine.
   */
  readonly backglass?: BackglassSurface
}

/**
 * A standard Williams-era body, for a definition that does not state its own.
 *
 * Here rather than beside the one caller that used to hold it, because it stopped being one
 * caller the moment the cabinet became something a player *touches*: `src/xr/cabinetControls.ts`
 * puts the flipper buttons 0.14 m back from the front and the plunger on the right-hand end of
 * it, and a second copy of 1.295 would put the buttons on a cabinet the renderer is not drawing.
 * That is the same defect class as the calibration rig's bat pose, and it is worth one export.
 */
export const STANDARD_CABINET = {
  length: 1.295,
  width: 0.724,
  height: 1.918,
  lockdownBarHeight: 0.914,
} as const

/**
 * The body a machine's own definition describes, with a standard one behind every gap.
 *
 * **One place rather than three.** The client assembled this block separately for the renderer,
 * for the XR rig and for the camera presets, each with its own `?? STANDARD_CABINET` fallbacks —
 * which is exactly the arrangement that puts the flipper buttons on a cabinet the renderer is not
 * drawing. Every consumer now gets the same body, including the shooter lane, which is the field
 * that made the duplication visible: the XR rig had one and the renderer did not.
 */
export function cabinetDimensionsFor(definition: MachineDefinition): CabinetDimensions {
  const value=definition.dimensions.playfieldRecess
  return readCabinetDimensions(definition, value!==undefined&&Number.isFinite(value)?Math.max(.005,Math.min(.15,value)):.03)
}

/** The accepted cards and clear control decals belong to the case, independently of board depth. */
export function instructionCabinetDimensionsFor(definition: MachineDefinition): CabinetDimensions {
  return readCabinetDimensions(definition,.03)
}

function readCabinetDimensions(definition: MachineDefinition, recess:number): CabinetDimensions {
  const dims = definition.dimensions
  const lane = shooterLane(definition)
  const body = {
    ...(recess===.03?{}:{playfieldRecess:recess,glassHeight:dims.glassHeight,playfieldWidth:dims.playfield.width}),
    length: dims.cabinet?.length ?? STANDARD_CABINET.length,
    width: dims.cabinet?.width ?? STANDARD_CABINET.width,
    height: dims.cabinet?.height ?? STANDARD_CABINET.height,
    lockdownBarHeight: dims.lockdownBarHeight ?? STANDARD_CABINET.lockdownBarHeight,
    incline: (dims.inclineDegrees * Math.PI) / 180,
    playfieldLength: dims.playfield.length,
  }
  return {
    ...body,
    ...(lane === undefined
      ? {}
      : { shooterX: lane.x, shooterY: shooterLaneHeight(body, lane.y), shooterZ: -lane.y * Math.cos(body.incline) }),
  }
}

/**
 * The height of a ball resting on the playfield at `alongY` (playfield-local y, metres from the
 * playfield's centre, +y upfield), in cabinet metres from the floor.
 *
 * The playfield's centre sits where `buildCabinet` puts it, and a point `alongY` up the incline
 * is `alongY · sin(incline)` higher; the ball's centre is a radius above the surface.
 */
export function shooterLaneHeight(dims: PlayfieldBody, alongY: number): number {
  return playfieldCentreY(dims) + alongY * Math.sin(dims.incline) + BALL_RADIUS
}

/** What placing the playfield in the cabinet needs to know. */
export interface PlayfieldBody {
  readonly playfieldRecess?: number
  readonly length: number
  readonly lockdownBarHeight: number
  readonly incline: number
  readonly playfieldLength: number
}

/** How far below the panels' top edge the playfield's centre is set, metres. */
const PLAYFIELD_BELOW_TOP = 0.03

/**
 * The body's front-to-back length: the stated cabinet, or long enough to contain the playfield's
 * footprint — its length as seen from above, which is shorter than the playfield by the incline —
 * with a panel's thickness at each end. Lengthened to fit rather than refused: a Builder machine
 * mid-edit is inconsistent constantly and still has to be looked at.
 */
export function bodyLengthFor(dims: PlayfieldBody): number {
  return Math.max(dims.length, dims.playfieldLength * Math.cos(dims.incline) + 2 * PLY)
}

/**
 * Where the playfield's centre sits, metres from the floor: halfway up the panels' top edge —
 * which runs from the lockdown bar at the front to one rise higher at the back — and a little
 * under it. One function, so the rod that pushes the ball (`shooterLaneHeight`) and the playfield
 * it pushes it along cannot disagree by a centimetre.
 */
export function playfieldCentreY(dims: PlayfieldBody): number {
  const frontTopY = dims.lockdownBarHeight
  const rise = bodyLengthFor(dims) * Math.tan(dims.incline)
  return frontTopY + rise / 2 - (dims.playfieldRecess ?? PLAYFIELD_BELOW_TOP)
}

/**
 * The x of the machine's plunger device, or nothing if it states none.
 *
 * The playfield's frame and the cabinet's share a centre line, so a device's own x is already the
 * distance from the middle of the cabinet — no conversion, and none hidden here either.
 *
 * **Superseded, and kept only as the named answer to that one question.** This was the primitive
 * until 14 Sep 2026, when the rod stopped being lined up across the machine and started being
 * aimed through the ball it launches: a lane has a height and a distance down the incline as well
 * as an x, so `shooterLane` returns the pair, `cabinetDimensionsFor` reads it once, and every
 * consumer takes `shooterX`/`shooterY`/`shooterZ` off `CabinetDimensions`. Nothing in the product
 * calls this, and `scripts/check-reachable.mjs` names it for that reason rather than hiding it —
 * **if you are reaching for a plunger position, you want `shooterLane` or the dimensions, not this.**
 */
export function shooterLaneX(definition: MachineDefinition): number | undefined {
  return shooterLane(definition)?.x
}

/** The machine's plunger device's centre, playfield-local, or nothing if it states none. */
export function shooterLane(definition: MachineDefinition): { readonly x: number; readonly y: number } | undefined {
  for (const device of definition.devices) {
    if (device.kind === 'plunger') return { x: device.centre[0], y: device.centre[1] }
  }
  return undefined
}

/** Panel thickness, metres. Real cabinets are 3/4-inch ply. */
const PLY = 0.019
/** Leg cross-section, metres. Two and a half inches square, as a real leg is. */
const LEG = 0.06
/**
 * The vertical depth of the cabinet body at its front face, metres.
 *
 * **The number that was missing.** `LEG` is a cross-section, and it was also used as the height the
 * legs lift the body by — one constant for two quantities an order of magnitude apart. The body was
 * drawn from 0.06 m up to the lockdown bar at 0.914 m: a 0.85 m box on 6 cm feet, where a real
 * machine is a shallow body on long legs. Reported from a headset on 30 Aug 2026 as *"the pinball
 * main section is on the floor instead of the bottom of the legs/feet"*.
 *
 * 7.5 inches, and checked rather than asserted: `lockdownBarHeight − BODY_DEPTH` is 0.7235 m and a
 * standard pinball leg is 28.5 inches, 0.7239 m. Two independent measurements of a real machine
 * agreeing to four tenths of a millimetre.
 *
 * Held identical to `MachineDimensions.BodyDepth` in `core/cs`; the two cabinets are the same
 * cabinet.
 */
const BODY_DEPTH = 0.1905
/** Shared solid-face limit for fitted cabinet labels. */
export function cabinetBottomY(dims: CabinetDimensions): number {
  return Math.max(LEG, dims.lockdownBarHeight - BODY_DEPTH)
}
/** Side-rail cross-section. The chrome strip a player's palms sit on. */
const RAIL = 0.024

/** What to leave out of a cabinet. Everything is built unless something asks for less. */
export interface CabinetParts {
  /**
   * Whether to build the head — the backbox, and the backglass on it (M8-84).
   *
   * **For the roster tile, which is a picture of the play area.** Its camera looks down the table
   * and the head is meant to be out of shot — and at M8-83 it was, for one of the two shipped
   * machines. IRON FATHOM's playfield is 50 mm wider, so its fit binds on the width and leaves a
   * little vertical slack, and the backglass came back in **row 0: a single pixel across the top
   * of the frame**. Framing it out works until a machine is proportioned slightly differently,
   * which a Machine Builder guarantees; not building it cannot fail that way.
   *
   * It is also most of what that picture was paying for. The backglass and the two cabinet flanks
   * are art programs, and rasterising them was 61 ms of a 116 ms render — measured — for pixels
   * that differ by at most 3 of 255.
   */
  readonly head?: boolean
}

/**
 * Build the cabinet.
 *
 * Returns the geometries and materials as well as the group, because a `SceneBuilder` has to be
 * able to dispose them: Three does not free GPU buffers when a mesh leaves the graph, and a
 * scene rebuilt on every context loss would leak one whole cabinet each time.
 */
export function buildCabinet(
  dims: CabinetDimensions,
  artTextures: CabinetArt = {},
  parts: CabinetParts = {},
): CabinetBuild {
  const group = new Group()
  const fitted=artTextures.illustrated ? authoredCabinetFor(artTextures.machineId,dims) : undefined
  const trim=cabinetTrimFor(artTextures.machineId,dims)
  // Named, so the picture of the controls can find the caps under the cabinet and not the
  // playfield's flipper devices of the same name (`controlPicture.ts`).
  group.name = 'cabinet'
  const geometries: BufferGeometry[] = []
  const materials: Material[] = []

  const halfWidth = dims.width / 2

  // The body has to contain the playfield's *footprint* — the playfield's length as seen from
  // above, which is shorter than the playfield itself by the incline. A definition that states a
  // cabinet shorter than its own playfield describes a machine that cannot be assembled, and
  // drawing it literally puts the far end of the playfield through the back panel. Lengthened to
  // fit rather than refused, for the same reason a missing cabinet block falls back to a standard
  // body: a Builder machine mid-edit is inconsistent constantly and still has to be looked at.
  const length = bodyLengthFor(dims)
  const halfLength = length / 2

  // From the machine's one palette rather than typed here, so the browser and the headset cannot
  // paint the same cabinet two different colours. `scripts/check-finish-parity.mjs` compares the
  // table these come from against the native client's.
  const body = FINISHES.cabinetBody
  const bodyMaterial = new MeshStandardMaterial(body)
  // Each flank gets its own art. A machine's definition states `cabinet.left` and
  // `cabinet.right` separately and NEON NOVA's differ — the wordmark sits at 0.68 across on one
  // side and 0.32 on the other, so that it reads from front to back on *both* flanks rather
  // than backwards on one. Painting the left art on both sides throws that away and puts a
  // mirrored wordmark on the right-hand panel, which is the tell of a machine drawn from one
  // photograph. Falls back to the other side, and then to bare body, so a machine that states
  // only one flank still gets art on both.
  const leftArt = artTextures.left ?? artTextures.right
  const rightArt = artTextures.right ?? artTextures.left
  const printed = FINISHES.printedCabinet
  const leftMaterial = leftArt ? new MeshStandardMaterial({ ...printed, map: leftArt }) : bodyMaterial
  const rightMaterial = rightArt ? new MeshStandardMaterial({ ...printed, map: rightArt }) : bodyMaterial
  const trimMaterial = new MeshStandardMaterial(FINISHES.cabinetTrim)
  const darkMaterial = new MeshStandardMaterial(FINISHES.cabinetDark)
  materials.push(bodyMaterial, trimMaterial, darkMaterial)
  if (leftMaterial !== bodyMaterial) materials.push(leftMaterial)
  if (rightMaterial !== bodyMaterial) materials.push(rightMaterial)

  // The body's top edge: level bottom at the leg height, front top at the lockdown bar, and a
  // back top one rise higher. Derived from the machine's own incline, never typed, so a
  // shallower machine gets a shallower cabinet.
  //
  // **The rise is across the cabinet, at the playfield's angle** — `length · tan(incline)`, and
  // not `playfieldLength · sin(incline)`. The second is the height the *playfield* climbs over
  // its own length, and spreading it across the longer cabinet produces a top edge at 5.33°
  // where the playfield is at 6.5°: an edge that is not parallel to the surface it is supposed
  // to be the side of. Nothing about the panel looks wrong on its own, but the side rails are
  // rotated by the real incline, and a rail on a shallower edge floats 13 mm off it at the back.
  // Derived from this machine's own bar height rather than from a leg length, so a cabinet stating
  // a lower bar gets shorter legs and keeps its body depth. Floored at `LEG` so an implausibly low
  // stated bar still leaves somewhere for the legs, rather than a body below the floor.
  const bottomY = cabinetBottomY(dims)
  const frontTopY = dims.lockdownBarHeight
  const rise = length * Math.tan(dims.incline)
  const backTopY = frontTopY + rise

  // --- side panels, as the quadrilateral they actually are -------------------------
  //
  // Drawn in the ZY plane and extruded across X, because a `Shape` is authored in XY.
  //
  // **The shape's x is the *negated* world z.** `rotateY(π / 2)` sends shape +X to world −Z —
  // that is what the rotation is — so a panel authored as though shape x were world z comes out
  // mirrored front to back: tall at the front, short at the back, disagreeing with the front and
  // back panels beside it by the whole 121 mm rise. It is measured rather than reasoned about,
  // in `test/world/cabinetSilhouette.test.ts`, because the error is a sign in a rotation nobody
  // can hold in their head and the result still looks like a pinball machine from most angles.
  const side = new Shape()
  side.moveTo(-halfLength, bottomY) // world z = +halfLength: the front, at the player
  side.lineTo(halfLength, bottomY) // world z = −halfLength: the back
  side.lineTo(halfLength, backTopY) // the back is the tall end
  side.lineTo(-halfLength, frontTopY) // and the front is the low one
  side.closePath()

  // One geometry per flank rather than one shared between them, because the two carry different
  // art and therefore different texture coordinates — see `paintSideUv`.
  for (const sign of [-1, 1] as const) {
    const sideGeometry = new ExtrudeGeometry(side, { depth: PLY, bevelEnabled: false })
    // Shape x → world −z, shape y → world y, extrusion depth → world +x.
    sideGeometry.rotateY(Math.PI / 2)
    paintSideUv(sideGeometry, sign, halfLength, length, bottomY, backTopY)
    geometries.push(sideGeometry)

    const panel = new Mesh(sideGeometry, sign < 0 ? leftMaterial : rightMaterial)
    panel.name = sign < 0 ? 'sideLeft' : 'sideRight'
    panel.position.x = sign * halfWidth - (sign > 0 ? PLY : 0)
    group.add(panel)
  }

  // --- front, back and floor -------------------------------------------------------
  const innerWidth = dims.width - 2 * PLY
  const front = box(innerWidth, frontTopY - bottomY, PLY, geometries)
  const frontMaterial = artTextures.front ? new MeshStandardMaterial({...printed, map: artTextures.front}) : bodyMaterial
  if (frontMaterial !== bodyMaterial) materials.push(frontMaterial)
  const frontPanel = new Mesh(front, frontMaterial)
  frontPanel.name = 'front'
  frontPanel.position.set(0, (frontTopY + bottomY) / 2, halfLength - PLY / 2)
  group.add(frontPanel)

  const back = box(innerWidth, backTopY - bottomY, PLY, geometries)
  const backPanel = new Mesh(back, bodyMaterial)
  backPanel.position.set(0, (backTopY + bottomY) / 2, -halfLength + PLY / 2)
  group.add(backPanel)

  const floor = box(innerWidth, PLY, length - 2 * PLY, geometries)
  const floorPanel = new Mesh(floor, darkMaterial)
  floorPanel.position.set(0, bottomY + PLY / 2, 0)
  group.add(floorPanel)

  // --- legs -------------------------------------------------------------------------
  // Inset from the corners the way real leg plates are, and tall enough to be the only thing
  // touching the floor.
  // **Tall enough to reach the body**, which is `bottomY` and not the leg's own cross-section.
  const fittedLegs = cabinetLegsReady(dims)
  if (!fittedLegs) {
  const legHeight = bottomY + 0.002
  const legGeometry = box(LEG, legHeight, LEG, geometries)
  for (const sx of [-1, 1] as const) {
    for (const sz of [-1, 1] as const) {
      const leg = new Mesh(legGeometry, trimMaterial)
      leg.userData['floorSupport'] = 'shaft'
      leg.position.set(sx * (halfWidth - LEG / 2 - 0.004), legHeight / 2, sz * (halfLength - LEG / 2 - 0.004))
      if (!artTextures.illustrated) group.add(leg)
      else if (!fitted) {
        const x=leg.position.x, z=leg.position.z
        for (const [w,d,dx,dz] of [[.003,LEG,sx*(LEG/2-.0015),0],[LEG,.003,0,sz*(LEG/2-.0015)]]) {
          const folded = new Mesh(box(w!,legHeight-.018,d!,geometries),trimMaterial)
          folded.userData['floorSupport'] = 'shaft'
          folded.position.set(x+dx!,legHeight/2+.009,z+dz!); group.add(folded)
        }
        for (const [radius,height,y,material] of [[.024,.01,.005,darkMaterial],[.006,.022,.018,trimMaterial]] as const) {
          const geometry = new CylinderGeometry(radius,radius,height,20); geometries.push(geometry)
          const foot=new Mesh(geometry,material); foot.position.set(x,y,z); group.add(foot)
          foot.userData['floorSupport'] = 'foot'
        }
        for(const y of [bottomY-.03,bottomY-.08]) {
          const geometry=new SphereGeometry(.005,10,6); geometries.push(geometry)
          const bolt=new Mesh(geometry,trimMaterial); bolt.position.set(x+sx*LEG/2,y,z); group.add(bolt)
        }
      }
    }
  }

  }
  // --- side rails and lockdown bar ----------------------------------------------------
  //
  // The rails follow the top edge, so they are rotated by the incline rather than laid flat.
  // A level rail on a tilted cabinet is the tell that a rendering was built from a photograph.
  //
  // **Positive, not negative.** Three's `rotation.x` sends local +Z towards +Y, and +Z here is
  // *towards the player* — the low end. A negative angle therefore lifts the front of the rail
  // and drops the back, crossing the panel's top edge by 73 mm at each end and leaving the rail
  // floating above the cabinet at one end and buried in it at the other.
  const railLength = Math.hypot(length, rise)
  const railGeometry = box(RAIL, RAIL, railLength, geometries)
  for (const sign of [-1, 1] as const) {
    const rail = new Mesh(railGeometry, trimMaterial)
    rail.position.set(sign * (halfWidth - RAIL / 2), (frontTopY + backTopY) / 2 + RAIL / 2, 0)
    rail.rotation.x = dims.incline
    if (!fitted && !trim?.legacyRails) group.add(rail)
  }

  const barGeometry = box(dims.width, 0.035, 0.055, geometries)
  const bar = new Mesh(barGeometry, trimMaterial)
  bar.position.set(0, frontTopY + 0.014, halfLength - 0.02)
  bar.rotation.x = dims.incline
  if (!fitted && !trim) group.add(bar)

  // --- backbox --------------------------------------------------------------------------
  //
  // Leaned back 8°, which is the angle that puts the backglass square to a standing player: the
  // player's eyes are above the middle of the glass and looking down at it, so the glass has to
  // face slightly *up* to meet them, which means the top of the box tips away from them.
  //
  // Negative, for the same reason the rails are positive. A positive angle here tips the top
  // towards the player and aims the backglass at their knees.
  const backboxHeight = dims.height - backTopY
  const wantsHead = parts.head !== false
  const shell = artTextures.illustrated ? authoredBackbox(artTextures.machineId,dims) : undefined
  if(shell)geometries.push(shell)
  const backboxGeometry = shell ?? box(dims.width, backboxHeight, 0.14, geometries)
  const backbox = new Mesh(backboxGeometry, bodyMaterial)
  backbox.position.set(0, backTopY + backboxHeight / 2 - 0.02, -halfLength + 0.055)
  backbox.rotation.x = -0.14
  if (wantsHead) group.add(backbox)

  let backglass: BackglassSurface | undefined
  if (wantsHead && artTextures.backglass) {
    const glassWidth = dims.width - 0.05
    const glassHeight = backboxHeight - 0.09
    const glassGeometry = box(glassWidth, glassHeight, 0.004, geometries)
    const glassMaterial = shell ? new MeshPhysicalMaterial({
      ...FINISHES.backglassArt,...reflectionFinish.backglass,
      map:artTextures.backglass,emissiveMap:artTextures.backglass,
    }) : new MeshStandardMaterial({
      ...FINISHES.backglassArt,
      map: artTextures.backglass,
      emissiveMap: artTextures.backglass,
    })
    materials.push(glassMaterial)
    const glass = new Mesh(glassGeometry, glassMaterial)
    glass.name = 'backglass'
    // Parented to the backbox so the 8° lean carries, rather than being applied twice.
    glass.position.set(0, 0.01, 0.073)
    backbox.add(glass)
    backglass = { mesh: glass, width: glassWidth, height: glassHeight }
  }

  buildControls(group, dims, geometries, materials)
  if (artTextures.illustrated && !fitted) {
    const coin = cabinetControlsFor(dims).coin.at, y=(frontTopY+bottomY)/2
    for (const [w,h,d,dz,material] of [[.27,.23,.006,.003,trimMaterial],[.258,.218,.005,.006,darkMaterial]] as const) {
      const panel=new Mesh(box(w,h,d,geometries),material)
      panel.name='coinDoor'; panel.position.set(coin.x,y,halfLength+dz); group.add(panel)
    }
    const geometry=new SphereGeometry(.007,12,8); geometries.push(geometry)
    const lock=new Mesh(geometry,trimMaterial); lock.position.set(coin.x+.090,y-.05,halfLength+.011); group.add(lock)
  }

  if(fitted){addAuthoredCabinet(fitted,group,geometries,materials);if(!fittedLegs)addFloorContact(artTextures.machineId,dims,group,geometries,materials)}
  if(fittedLegs)addCabinetLegs(dims,group,geometries,materials)
  if(trim) addCabinetTrim(trim,group,geometries,materials)
  if(artTextures.illustrated){const glazing=authoredGlazingFor(artTextures.machineId,dims);if(glazing)addAuthoredGlazing(glazing,group,geometries,materials)}
  assembleCabinetPlunger(group,dims,geometries,materials)
  assembleCabinetControls(group,dims,fitted!==undefined,geometries,materials)
  return {
    group,
    playfieldCentre: { y: playfieldCentreY(dims), z: 0 },
    geometries,
    materials,
    // The body's paint, and only that — see `CabinetBuild.tintable`. De-duplicated because a
    // machine that states no flank art paints both sides with `bodyMaterial` itself, and tinting
    // one material three times would cube the tint.
    tintable: [bodyMaterial, leftMaterial, rightMaterial, frontMaterial, trimMaterial, darkMaterial].filter(
      (material, at, all) => all.indexOf(material) === at,
    ),
    ...(backglass !== undefined ? { backglass } : {}),
  }
}

/** How far a button's cap stands proud of the panel it goes through, metres. */
export const BUTTON_PROUD = 0.012

/** The rail lamps above the flipper buttons, metres. A real indicator lamp is about this. */
export const LAMP_RADIUS = 0.007

/** The plunger rod's radius, metres. A real shooter rod is about 9 mm across. */
export const PLUNGER_ROD_RADIUS = 0.0045

/** The knob on the end of it, metres. */
export const PLUNGER_KNOB_RADIUS = 0.019

/**
 * The buttons and the plunger, drawn where the interaction already says they are.
 *
 * **They were never drawn — in either client.** `cabinetControls.ts` has placed the flipper
 * buttons, the start button and the coin slot since M5-05, and `pressGate` has been detecting
 * presses on them the whole time, against geometry that did not exist. From a headset that reads
 * as "flipper buttons are not visible to user and flipper buttons are not reachable by user", and
 * the second half is caused by the first: a 30 mm target nobody can see is not a target, it is a
 * guess. Found on the Quest and fixed in both clients, because the gap was identical in both.
 *
 * Built by asking `cabinetControlsFor` rather than by repeating its offsets here, so the button a
 * player reaches for and the button the state machine is watching are the same button by
 * construction. Two copies of `frontZ − 0.14` would drift by a centimetre one day and the machine
 * would simply feel unresponsive near the edges, which is close to undebuggable from a screenshot.
 */
function buildControls(
  group: Group,
  dims: CabinetDimensions,
  geometries: BufferGeometry[],
  materials: Material[],
): void {
  const controls = cabinetControlsFor(dims)
  // Through the palette rather than `new MeshStandardMaterial(FINISHES.chrome)`: chrome asks for
  // a clearcoat, which only `MeshPhysicalMaterial` has, and building it by hand as a standard
  // material silently drops the one property that makes chrome look like chrome.
  const palette = new PartPalette()
  const chrome = palette.get('chrome')
  const flipperFace = palette.get('targetFace')
  // **The start button is lit**, as a real one is (14 Sep 2026: "The start button is not
  // visible, it is too dark for the user to see the button (only highlight shows)"). It was
  // chrome, which is a mirror of the room, and in passthrough the room it mirrors is dark. A
  // cream cap with its own light reads on any wall; `controlPicture.ts` burns it brighter while
  // it is pressed.
  const startMaterial = new MeshStandardMaterial({
    color: 0xfff1cf,
    emissive: 0xffd27a,
    emissiveIntensity: START_GLOW_REST,
    roughness: 0.45,
    metalness: 0,
  })
  materials.push(startMaterial)

  for (const id of ['flipperLeft', 'flipperRight', 'start', 'coin'] as const) {
    const control = controls[id]
    // **Drawn at the cap's own radius, not at the reach** (M8-28). `control.radius` is how far
    // off-axis a fingertip may be and still press, which is deliberately larger than the
    // button — see `BUTTON_REACH`. The flippers are the exception since 14 Sep 2026: their cap is
    // drawn at `FLIPPER_CAP_RADIUS`, the zone's own size near enough, because a player looking at
    // the ball finds the button by the picture in the corner of their eye, and a picture a third
    // the size of the target had them guessing.
    const radius = isFlipper(id) ? FLIPPER_CAP_RADIUS : BUTTON_RADIUS
    const cap = new CylinderGeometry(radius, radius * 0.92, BUTTON_PROUD * 2, 20)
    // Three builds a cylinder along its own +Y, and the four buttons face three different ways:
    // the flippers sideways, start upward off the bar, the coin slot forward. A cylinder is
    // symmetric about its axis, so only the axis matters and not which way along it the button
    // points — which is why these are unsigned tests on a direction that has a sign.
    if (Math.abs(control.inward.x) > 0.5) cap.rotateZ(Math.PI / 2)
    else if (Math.abs(control.inward.z) > 0.5) cap.rotateX(Math.PI / 2)
    geometries.push(cap)

    const mesh = new Mesh(cap, isFlipper(id) ? flipperFace : id === 'start' ? startMaterial : chrome)
    mesh.name = id
    mesh.position.set(control.at.x, control.at.y, control.at.z)
    group.add(mesh)
  }

  // **A lamp on the rail above each flipper button** (14 Sep 2026: "add an indicator that lets
  // the user know their hand is positioned on the flipper properly … a little green light …
  // close to the side of flipper"). The buttons are on the flanks, below the rail, out of the eye's
  // way while it watches the ball; the rail top is in the corner of that eye. Set into the rail's
  // top face directly above the button, dark until `controlPicture.ts` lights it: green with a
  // fingertip in the zone, white while pressed. One material each, because they light separately.
  const lampGeometry = new SphereGeometry(LAMP_RADIUS, 12, 8)
  geometries.push(lampGeometry)
  const halfLength = bodyLengthFor(dims) / 2
  const buttonZ = halfLength - BUTTON_SET_BACK
  // The rail's top face at the button's z: the panels' top edge climbs from the bar at the front,
  // and the rail sits a rail's thickness above it.
  const lampY = dims.lockdownBarHeight + (halfLength - buttonZ) * Math.tan(dims.incline) + RAIL + LAMP_RADIUS / 2
  for (const [sign, name] of [[-1, 'lampFlipperLeft'], [1, 'lampFlipperRight']] as const) {
    const lampMaterial = new MeshStandardMaterial({
      color: LAMP_OFF,
      emissive: LAMP_OFF,
      emissiveIntensity: LAMP_GLOW_OFF,
      roughness: 0.4,
    })
    materials.push(lampMaterial)
    const lamp = new Mesh(lampGeometry, lampMaterial)
    lamp.name = name
    lamp.position.set(sign * (dims.width / 2 - RAIL / 2), lampY, buttonZ)
    group.add(lamp)
  }

  // The plunger: one rod, from the rubber tip resting against the ball to the knob outside the
  // front, along the lane's own axis. Drawn for the same reason as the buttons — the grab radius
  // is 45 mm around a point that had nothing at it — and as one piece because it was two (14 Sep
  // 2026): the playfield drew a stub in the lane and the cabinet a level rod through the front,
  // and "pinball plunger looks odd, doesn't line up properly with the inside plunger and outside
  // plunger". `controlPicture.ts` slides all three parts along the axis when the rod is drawn.
  const { at: knobAt, axis, tip } = controls.plunger
  const along = new Vector3(axis.x, axis.y, axis.z)
  const rodLength = Math.max(0.01, Math.hypot(knobAt.x - tip.x, knobAt.y - tip.y, knobAt.z - tip.z))
  const rod = new CylinderGeometry(PLUNGER_ROD_RADIUS, PLUNGER_ROD_RADIUS, rodLength, 12)
  geometries.push(rod)
  const rodMesh = new Mesh(rod, chrome)
  rodMesh.name = 'plungerRod'
  // The cylinder is built along its own +Y; turned onto the axis, centred between tip and knob.
  rodMesh.quaternion.setFromUnitVectors(new Vector3(0, 1, 0), along)
  rodMesh.position.set((tip.x + knobAt.x) / 2, (tip.y + knobAt.y) / 2, (tip.z + knobAt.z) / 2)
  group.add(rodMesh)

  const knob = new SphereGeometry(PLUNGER_KNOB_RADIUS, 16, 10)
  geometries.push(knob)
  // Its own material, so `controlPicture.ts` can light it while a hand is in the grab zone.
  const knobMaterial = new MeshStandardMaterial({ ...FINISHES.cabinetTrim, emissive: LAMP_OVER, emissiveIntensity: 0 })
  materials.push(knobMaterial)
  const knobMesh = new Mesh(knob, knobMaterial)
  knobMesh.name = 'plungerKnob'
  knobMesh.position.set(knobAt.x, knobAt.y, knobAt.z)
  group.add(knobMesh)

  const tipGeometry = new SphereGeometry(PLUNGER_TIP_RADIUS, 12, 8)
  geometries.push(tipGeometry)
  const tipMesh = new Mesh(tipGeometry, palette.get('rubber'))
  tipMesh.name = 'plungerTip'
  tipMesh.position.set(tip.x, tip.y, tip.z)
  group.add(tipMesh)

  // **The pull gauge** (26 Sep 2026, the owner's shooter polish): a row of lamps on the front
  // panel under the rod, dark until `controlPicture.ts` lights them with the pull. One material
  // each, because they light separately.
  const gaugeGeometry = box(PULL_GAUGE_WIDTH, PULL_GAUGE_HEIGHT, PULL_GAUGE_DEPTH, geometries)
  pullGaugeOf(controls).forEach((lampAt, i) => {
    const gaugeMaterial = new MeshStandardMaterial({
      color: GAUGE_OFF,
      emissive: GAUGE_OFF,
      emissiveIntensity: 0,
      roughness: 0.4,
    })
    materials.push(gaugeMaterial)
    const lamp = new Mesh(gaugeGeometry, gaugeMaterial)
    lamp.name = `pullGauge${i}`
    lamp.position.set(lampAt.x, lampAt.y, lampAt.z)
    group.add(lamp)
  })

  // **The outer spring** (the same polish): a chrome coil on the rod from the front panel's outer
  // face to the back of the knob, where a player sees it. It stays anchored at the panel and
  // `controlPicture.ts` stretches it with the pull by scaling its own y, which is its length.
  const spring = plungerSpringOf(controls.plunger)
  if (spring !== undefined) {
    const outward = new Vector3(spring.direction.x, spring.direction.y, spring.direction.z)
    const path = plungerSpringPath(spring.rest).map((p) => new Vector3(p.x, p.y, p.z))
    const coil = new TubeGeometry(new CatmullRomCurve3(path), path.length, PLUNGER_SPRING_WIRE, 6, false)
    geometries.push(coil)
    const springGroup = new Group()
    springGroup.name = 'plungerSpring'
    springGroup.quaternion.setFromUnitVectors(new Vector3(0, 1, 0), outward)
    springGroup.position.set(spring.anchor.x, spring.anchor.y, spring.anchor.z)
    springGroup.add(new Mesh(coil, chrome))
    group.add(springGroup)
  }

  materials.push(...palette.all())
}

function isFlipper(id: PressableId): boolean {
  return id === 'flipperLeft' || id === 'flipperRight'
}

/**
 * The backglass, for anything that has to be fitted *to* it — the DMD, above all.
 *
 * Its own size rather than the backbox's, because the glass is inset and a machine's
 * backglass art is painted on the glass. Placing a display against the backbox's dimensions
 * puts it 50 mm wider than the picture it is supposed to sit inside.
 */
export interface BackglassSurface {
  readonly mesh: Mesh
  readonly width: number
  readonly height: number
}

export interface CabinetArt {
  readonly machineId?: string
  /** Trusted packaged illustration enables the fitted coastal cabinet hardware. */
  readonly illustrated?: boolean
  /** `art.cabinet.left`. Falls back to the right flank's art when a machine states only one. */
  readonly left?: import('three').Texture
  /** `art.cabinet.right`. A machine's two flanks are not the same picture. */
  readonly right?: import('three').Texture
  /** Exterior front print around the controls, from `frontApron` or the earlier `front`. */
  readonly front?: import('three').Texture
  readonly backglass?: import('three').Texture
}

/**
 * Normalised texture coordinates for a side panel, running the right way round.
 *
 * `ExtrudeGeometry`'s default UV generator returns the shape's own coordinates — **in metres**.
 * On a 1.295 m cabinet that puts `u` between −0.647 and 1.062, so with the default clamped wrap
 * the whole front half of each flank samples one column of edge pixels and the art appears
 * squashed into the rear half. Nothing about that reads as "the texture coordinates are wrong";
 * it reads as "the cabinet art is a bit smeary".
 *
 * **`u` runs the way the art is read, which is not the same direction on both flanks.** Seen
 * from outside the left panel, the front of the machine is on the viewer's right; seen from
 * outside the right panel it is on the viewer's left. A machine's two side artworks are mirror
 * images of each other for exactly this reason — NEON NOVA puts its wordmark at 0.68 across on
 * the left and 0.32 on the right — and mapping both flanks the same way un-mirrors one of them,
 * which prints the wordmark backwards on that side.
 */
function paintSideUv(
  geometry: BufferGeometry,
  sign: -1 | 1,
  halfLength: number,
  length: number,
  bottomY: number,
  topY: number,
): void {
  const position = geometry.getAttribute('position')
  const uv = geometry.getAttribute('uv')
  const normal = geometry.getAttribute('normal')
  const height = topY - bottomY
  for (let i = 0; i < position.count; i++) {
    const z = position.getZ(i)
    const u = sign < 0 ? (z + halfLength) / length : (halfLength - z) / length
    // End grain uses a single adjacent print swatch, avoiding squeezed image columns.
    uv.setXY(i, u, Math.abs(normal.getZ(i)) > .5 ? 0 : (position.getY(i) - bottomY) / height)
  }
  uv.needsUpdate = true
}

function box(x: number, y: number, z: number, sink: BufferGeometry[]): BoxGeometry {
  const g = new BoxGeometry(x, y, z)
  sink.push(g)
  return g
}
